import api from '../../../lib/api';
import type {
  AgentChatMessage,
  AgentConfig,
  AgentExecution,
  AgentSkill,
  AgentToolAction,
  AgentUsageSummary,
  AvailableToolsResponse,
  BudgetStatus,
  CacheStats,
  CommsCostDashboard,
  CostBreakdown,
  CreateSkillInput,
  DeleteSkillInput,
  ExecuteAgentInput,
  ExecuteAgentResponse,
  ExecutePipelineInput,
  LeadAgentStatus,
  OrgAgentSkill,
  PaginatedChatMessages,
  PaginatedExecutions,
  PaginatedToolActions,
  PlanUsage,
  PromptPreview,
  QueryChatMessagesParams,
  QueryExecutionsParams,
  QueueStatus,
  SendChatMessageInput,
  SuggestCustomInstructionsInput,
  SuggestCustomInstructionsResponse,
  ToggleSkillInput,
  UpdateAgentConfigInput,
  UpdateCustomInstructionsInput,
  UpdateSkillInput,
} from '../types';

export const agentsApi = {
  execute: (input: ExecuteAgentInput) =>
    api.post<ExecuteAgentResponse>('/agents/execute', input).then((res) => res.data),

  listExecutions: (params: QueryExecutionsParams) =>
    api.get<PaginatedExecutions>('/agents/executions', { params }).then((res) => res.data),

  getExecution: (id: string, organizationId: string) =>
    api
      .get<AgentExecution>(`/agents/executions/${id}`, { params: { organizationId } })
      .then((res) => res.data),

  cancelExecution: (id: string, organizationId: string) =>
    api
      .post<AgentExecution>(`/agents/executions/${id}/cancel`, undefined, {
        params: { organizationId },
      })
      .then((res) => res.data),

  getConfig: (organizationId: string) =>
    api.get<AgentConfig>('/agents/config', { params: { organizationId } }).then((res) => res.data),

  updateConfig: (input: UpdateAgentConfigInput) =>
    api.patch<AgentConfig>('/agents/config', input).then((res) => res.data),

  getUsage: (organizationId: string) =>
    api
      .get<AgentUsageSummary>('/agents/usage', { params: { organizationId } })
      .then((res) => res.data),

  getPlanUsage: (organizationId: string) =>
    api.get<PlanUsage>('/usage/plan', { params: { organizationId } }).then((res) => res.data),

  getQueueStatus: () => api.get<QueueStatus>('/agents/queue/status').then((res) => res.data),

  sendChatMessage: (input: SendChatMessageInput) =>
    api.post<AgentChatMessage>('/agents/chat', input).then((res) => res.data),

  getChatMessages: (params: QueryChatMessagesParams) =>
    api.get<PaginatedChatMessages>('/agents/chat/messages', { params }).then((res) => res.data),

  getSkills: (agentType?: string) =>
    api.get<AgentSkill[]>('/agents/skills', { params: { agentType } }).then((res) => res.data),

  getOrgSkills: (organizationId: string, agentType: string) =>
    api
      .get<OrgAgentSkill[]>('/agents/skills/org', { params: { organizationId, agentType } })
      .then((res) => res.data),

  toggleSkill: (input: ToggleSkillInput) =>
    api
      .patch(`/agents/skills/${input.skillId}/toggle`, {
        organizationId: input.organizationId,
        enabled: input.enabled,
        userId: input.userId,
        executionMode: input.executionMode,
      })
      .then((res) => res.data),

  createSkill: (input: CreateSkillInput) =>
    api.post<AgentSkill>('/agents/skills', input).then((res) => res.data),

  updateSkill: (input: UpdateSkillInput) =>
    api
      .patch<AgentSkill>(`/agents/skills/${input.skillId}`, {
        organizationId: input.organizationId,
        name: input.name,
        description: input.description,
        promptSnippet: input.promptSnippet,
        category: input.category,
        sortOrder: input.sortOrder,
        toolNames: input.toolNames,
        executionMode: input.executionMode,
        maxToolCalls: input.maxToolCalls,
      })
      .then((res) => res.data),

  deleteSkill: (input: DeleteSkillInput) =>
    api
      .delete(`/agents/skills/${input.skillId}`, {
        params: { organizationId: input.organizationId },
      })
      .then((res) => res.data),

  updateCustomInstructions: (input: UpdateCustomInstructionsInput) =>
    api.patch<AgentConfig>('/agents/custom-instructions', input).then((res) => res.data),

  // Per-request override only, same reasoning as onboardingApi.generatePlan:
  // the server bounds this call with its own 90s single-attempt timeout, which
  // outlives the global 30s axios default. Without this the browser aborts
  // first on a slow-but-valid suggestion — the user sees a failure, the server
  // still finishes, and the tokens are spent either way. 100s leaves headroom
  // over the server deadline so the server's own error is what reaches the UI.
  // Keep this ABOVE SUGGEST_TIMEOUT_MS whenever that moves, and do NOT raise
  // the global default in lib/api.ts for this.
  suggestCustomInstructions: (input: SuggestCustomInstructionsInput) =>
    api
      .post<SuggestCustomInstructionsResponse>('/agents/custom-instructions/suggest', input, {
        timeout: 100000,
      })
      .then((res) => res.data),

  getPromptPreview: (organizationId: string, agentType: string) =>
    api
      .get<PromptPreview>('/agents/prompt-preview', { params: { organizationId, agentType } })
      .then((res) => res.data),

  executePipeline: (input: ExecutePipelineInput) =>
    api.post<ExecuteAgentResponse[]>('/agents/pipeline', input).then((res) => res.data),

  getLeadAgentStatus: (leadId: string, organizationId: string) =>
    api
      .get<LeadAgentStatus>(`/agents/lead-status/${leadId}`, { params: { organizationId } })
      .then((res) => res.data),

  cancelLeadPipeline: (leadId: string, organizationId: string) =>
    api
      .post<{ cancelledCount: number }>(`/agents/lead-status/${leadId}/cancel`, undefined, {
        params: { organizationId },
      })
      .then((res) => res.data),

  getAvailableTools: () =>
    api.get<AvailableToolsResponse>('/agents/available-tools').then((res) => res.data),

  approveToolAction: (actionId: string) =>
    api.post<AgentToolAction>(`/agents/tool-actions/${actionId}/approve`).then((res) => res.data),

  rejectToolAction: (actionId: string, reason?: string) =>
    api.post(`/agents/tool-actions/${actionId}/reject`, { reason }).then((res) => res.data),

  listToolActions: (params: {
    organizationId: string;
    executionId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) => api.get<PaginatedToolActions>('/agents/tool-actions', { params }).then((res) => res.data),
};

export const modelRouterApi = {
  getCosts: (organizationId: string, month?: number, year?: number) =>
    api
      .get<CostBreakdown>(`/model-router/costs/${organizationId}`, {
        params: { month, year },
      })
      .then((res) => res.data),

  getCacheStats: () => api.get<CacheStats>('/model-router/cache/stats').then((res) => res.data),
};

export const communicationsApi = {
  getCostDashboard: (organizationId: string) =>
    api
      .get<CommsCostDashboard>('/communications/dashboard', {
        params: { organizationId },
      })
      .then((res) => res.data),

  getBudgetStatus: (organizationId: string) =>
    api
      .get<BudgetStatus>('/communications/budget', {
        params: { organizationId },
      })
      .then((res) => res.data),
};
